/**
 * Kitchen hours & food-booking cutoffs — Europe/Dublin.
 *
 * Rules (hard-coded for now, can move to Settings later):
 *   Mon–Thu : kitchen closes 21:00, food bookings accepted until 20:00
 *   Fri–Sat : kitchen closes 22:00, food bookings accepted until 21:00
 *   Sun     : kitchen closes 20:00, food bookings accepted until 19:00
 *
 * A booking is considered a "food booking" whenever it includes food service.
 */

export type BookingTypeForKitchen =
  | "pool_table"
  | "food_and_drinks"
  | "sports_viewing"
  | "darts"
  | "group_booking"
  | "karaoke";

/** Booking types that include food service and must respect the kitchen cutoff. */
export const FOOD_BOOKING_TYPES: ReadonlySet<BookingTypeForKitchen> = new Set([
  "food_and_drinks",
  "group_booking",
]);

export function bookingIncludesFood(type: BookingTypeForKitchen): boolean {
  return FOOD_BOOKING_TYPES.has(type);
}

/** 0 = Sunday, 1 = Monday … 6 = Saturday, in Europe/Dublin. */
function dublinWeekday(iso: string): number {
  const wd = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Dublin",
    weekday: "short",
  }).format(new Date(iso));
  return { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[wd] ?? 0;
}

/** Minutes since midnight in Europe/Dublin for an ISO timestamp. */
function dublinMinutes(iso: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Dublin",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
  const h = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
  const m = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
  return h * 60 + m;
}

/** Returns { cutoffMinutes, kitchenCloseMinutes, label } for a given ISO date. */
export function kitchenRulesFor(iso: string): {
  cutoffMinutes: number;
  kitchenCloseMinutes: number;
  cutoffLabel: string;
  kitchenCloseLabel: string;
  dayLabel: string;
} {
  const wd = dublinWeekday(iso);
  // Sun
  if (wd === 0) {
    return {
      cutoffMinutes: 19 * 60,
      kitchenCloseMinutes: 20 * 60,
      cutoffLabel: "7:00 PM",
      kitchenCloseLabel: "8:00 PM",
      dayLabel: "Sunday",
    };
  }
  // Fri, Sat
  if (wd === 5 || wd === 6) {
    return {
      cutoffMinutes: 21 * 60,
      kitchenCloseMinutes: 22 * 60,
      cutoffLabel: "9:00 PM",
      kitchenCloseLabel: "10:00 PM",
      dayLabel: wd === 5 ? "Friday" : "Saturday",
    };
  }
  // Mon–Thu
  return {
    cutoffMinutes: 20 * 60,
    kitchenCloseMinutes: 21 * 60,
    cutoffLabel: "8:00 PM",
    kitchenCloseLabel: "9:00 PM",
    dayLabel: ["", "Monday", "Tuesday", "Wednesday", "Thursday"][wd] ?? "Weekday",
  };
}

/**
 * Returns null if the food booking is allowed, or a human-readable error string
 * explaining why the requested time is past the kitchen cutoff.
 * Non-food bookings always return null.
 */
export function validateFoodBookingTime(
  type: BookingTypeForKitchen,
  startsAtIso: string,
): string | null {
  if (!bookingIncludesFood(type)) return null;
  const rules = kitchenRulesFor(startsAtIso);
  const startMin = dublinMinutes(startsAtIso);
  if (startMin > rules.cutoffMinutes) {
    return `Food bookings on ${rules.dayLabel} must start by ${rules.cutoffLabel} — the kitchen closes at ${rules.kitchenCloseLabel}.`;
  }
  return null;
}

/** For a given YYYY-MM-DD date + HH:MM time (Dublin local), check the cutoff. */
export function isTimeAfterCutoff(
  type: BookingTypeForKitchen,
  date: string,
  time: string,
): boolean {
  if (!bookingIncludesFood(type)) return false;
  // Build an ISO-ish string in Dublin local; Intl formatters above rely on ISO,
  // so we can approximate by treating "YYYY-MM-DDTHH:MM:00" as local.
  const iso = new Date(`${date}T${time}:00`).toISOString();
  return validateFoodBookingTime(type, iso) !== null;
}
